import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const modules = [
  { icon: "📝", en: "AI Script System", zh: "AI剧本系统", cap: "50–100 scripts/mo", desc: "AI-driven plot generation, character design, hook analysis & reversal optimization. Trend-matching story selection every week.", color: "#FF1C35" },
  { icon: "📣", en: "AI Ad System", zh: "AI广告系统", cap: "5,000+ creatives/mo", desc: "AI ad slicing, cover generation, copy optimization & programmatic bidding. Full-funnel automation from content to conversion.", color: "#e05c5c" },
  { icon: "🌐", en: "AI Localization", zh: "AI本地化系统", cap: "10+ languages", desc: "AI translation, subtitles, dubbing & cultural adaptation. Enables simultaneous global release across SEA markets.", color: "#c8a96e" },
  { icon: "🎯", en: "AI Recommendation", zh: "AI推荐系统", cap: "+50% retention", desc: "User interest profiling, content preference modeling & Watch Time optimization. Personalized feeds that keep users hooked.", color: "#7bc4e0" },
  { icon: "📡", en: "AI Marketing Engine", zh: "AI营销系统", cap: "500+ KOL network", desc: "KOL matching, traffic forecasting & community automation. Covers all channels without manual coordination overhead.", color: "#82c996" },
  { icon: "📊", en: "AI Data Analytics", zh: "AI数据分析系统", cap: "10M+ user tags", desc: "Real-time user behavior monitoring, ROI diagnostics & regional performance analytics. Data flywheel that compounds.", color: "#b07be0" },
];

const sop = [
  { step: "01", en: "Topic Selection", zh: "选题系统", time: "Daily", desc: "AI scans TikTok trends + regional user preferences → 20–50 new story directions/week.", icon: "🔍" },
  { step: "02", en: "Script SOP", zh: "剧本SOP", time: "Day 1–4", desc: "AI draft (1 day) → Editor polish (1–2 days) → Hook optimization (1 day) → Regional localization (1 day).", icon: "✍️" },
  { step: "03", en: "Filming SOP", zh: "拍摄SOP", time: "Day 4–8", desc: "Fixed-camera templates + rapid scene switching + AI-assisted storyboarding. 3–7 days per episode.", icon: "🎥" },
  { step: "04", en: "AI Editing SOP", zh: "AI剪辑SOP", time: "Day 8–11", desc: "Hook detection → auto-slicing → ad creative generation → multi-language covers. 50–200 ad assets per title.", icon: "⚡" },
];

const AIInfrastructure = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="ai" ref={ref} style={{ padding: "110px 40px", borderTop: "1px solid rgba(255,255,255,0.05)", position: "relative", overflow: "hidden" }}>
      {/* BG glow */}
      <div style={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", width: 800, height: 400, borderRadius: "50%", background: "radial-gradient(ellipse, rgba(255,28,53,0.06) 0%, transparent 70%)", filter: "blur(60px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 36, height: 2, background: "#FF1C35" }} />
            <span style={{ color: "#FF1C35", fontSize: 12, fontWeight: 700, letterSpacing: 3 }}>AI INFRASTRUCTURE · AI娱乐工业体系</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "end", marginBottom: 60 }} className="ai-header-grid">
            <div>
              <h2 style={{ fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, lineHeight: 1.08, margin: "0 0 18px" }}>
                MIRE Is Not a Studio.<br />
                <span style={{ color: "#FF1C35" }}>It's an AI Entertainment</span><br />
                Operating System.
              </h2>
              <p style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", lineHeight: 1.8, margin: 0 }}>
                Six proprietary AI modules form a fully integrated content industrial pipeline — from story ideation to global distribution — cutting production costs by 50%+ and generating 5,000+ ad creatives per month.
              </p>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.25)", lineHeight: 1.8, marginTop: 10 }}>
                MIRE不是传统影视公司，而是通过AI重构娱乐行业基础设施的科技型平台。
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              {[["50%+", "Cost Reduction"], ["5,000+", "Ad Assets/Mo"], ["10+", "Languages"], ["60%+", "AI Coverage"], ["50%+", "Retention Lift"], ["1 Day", "Script Draft"]].map(([v, l]) => (
                <div key={l} style={{ padding: "16px 12px", borderRadius: 12, background: "rgba(255,28,53,0.06)", border: "1px solid rgba(255,28,53,0.15)", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: "#FF1C35", fontFamily: "JetBrains Mono,monospace" }}>{v}</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 4, letterSpacing: 0.5 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* 6 AI Modules */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 64 }} className="ai-modules-grid">
          {modules.map((m, i) => (
            <motion.div key={m.en}
              initial={{ opacity: 0, y: 24 }} animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.09, duration: 0.6 }}
              style={{ padding: "26px 22px", borderRadius: 16, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", transition: "all 0.3s", cursor: "default" }}
              whileHover={{ background: "rgba(255,28,53,0.05)", borderColor: "rgba(255,28,53,0.25)" } as never}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <span style={{ fontSize: 30 }}>{m.icon}</span>
                <span style={{ padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}30`, fontFamily: "JetBrains Mono,monospace" }}>{m.cap}</span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginBottom: 3 }}>{m.en}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 10 }}>{m.zh}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.7 }}>{m.desc}</div>
            </motion.div>
          ))}
        </div>

        {/* SOP Pipeline */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.6, duration: 0.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
            <div style={{ width: 24, height: 2, background: "#FF1C35" }} />
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 700, letterSpacing: 3 }}>CONTENT SOP · 标准化生产流程</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }} className="sop-grid">
            {sop.map((s, i) => (
              <motion.div key={s.step}
                initial={{ opacity: 0, x: -16 }} animate={visible ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.7 + i * 0.1, duration: 0.55 }}
                style={{ padding: "20px 18px", borderRadius: 14, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", position: "relative", overflow: "hidden" }}
              >
                <div style={{ position: "absolute", top: -10, right: -6, fontSize: 52, fontWeight: 900, color: "rgba(255,28,53,0.07)", fontFamily: "JetBrains Mono,monospace", lineHeight: 1 }}>{s.step}</div>
                <div style={{ fontSize: 22, marginBottom: 10 }}>{s.icon}</div>
                <div style={{ fontSize: 10, color: "#FF1C35", fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>{s.time}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{s.en}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 8 }}>{s.zh}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", lineHeight: 1.65 }}>{s.desc}</div>
                {i < sop.length - 1 && (
                  <div style={{ position: "absolute", right: -8, top: "50%", transform: "translateY(-50%)", color: "rgba(255,28,53,0.4)", fontSize: 16, zIndex: 2 }}>→</div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom statement */}
        <motion.div initial={{ opacity: 0 }} animate={visible ? { opacity: 1 } : {}} transition={{ delay: 1.1 }}
          style={{ marginTop: 40, padding: "22px 32px", borderRadius: 14, background: "linear-gradient(135deg,rgba(255,28,53,0.08),rgba(255,28,53,0.03))", border: "1px solid rgba(255,28,53,0.18)", textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: 15, color: "rgba(255,255,255,0.75)", fontStyle: "italic" }}>
            "Unlike purely AI-generated content, MIRE's hybrid model combines <strong style={{ color: "#FF1C35" }}>AI efficiency</strong> with <strong style={{ color: "#FF1C35" }}>real filming</strong> — delivering both industrial scalability and genuine emotional authenticity."
          </p>
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "rgba(255,255,255,0.28)" }}>AI覆盖率目标 60%+ · 内容成本降低 50%+ · 月广告素材产能 5,000+</p>
        </motion.div>
      </div>
      <style>{`
        @media(max-width:900px){.ai-header-grid{grid-template-columns:1fr!important;gap:32px!important}.ai-modules-grid{grid-template-columns:1fr!important}.sop-grid{grid-template-columns:1fr 1fr!important}}
      `}</style>
    </section>
  );
};
export default AIInfrastructure;
